// Alert field definitions for the smart flow analysis system

export interface AlertField {
  key: string;
  label: string;
  type: 'number' | 'string' | 'boolean';
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
}

export const ALL_FIELDS: AlertField[] = [
  // Basic contract fields
  {
    key: 'strike_price',
    label: 'Strike Price',
    type: 'number',
    min: 0,
    step: 0.01
  },
  {
    key: 'volume',
    label: 'Volume',
    type: 'number',
    min: 0,
    step: 1
  },
  {
    key: 'open_interest',
    label: 'Open Interest',
    type: 'number',
    min: 0,
    step: 1
  },
  {
    key: 'last_price',
    label: 'Last Price',
    type: 'number',
    min: 0,
    step: 0.01
  },
  {
    key: 'bid',
    label: 'Bid',
    type: 'number',
    min: 0,
    step: 0.01
  },
  {
    key: 'ask',
    label: 'Ask',
    type: 'number',
    min: 0,
    step: 0.01
  },
  {
    key: 'underlying_price',
    label: 'Underlying Price',
    type: 'number',
    min: 0,
    step: 0.01
  },
  
  // Flow analysis fields
  {
    key: 'flow_score',
    label: 'Flow Score',
    type: 'number',
    min: 0,
    max: 100,
    step: 0.1
  },
  {
    key: 'premium_size',
    label: 'Premium Size',
    type: 'number',
    min: 0,
    step: 0.01
  },
  {
    key: 'premium',
    label: 'Premium (alias)',
    type: 'number',
    min: 0,
    step: 0.01
  },
  {
    key: 'execution_side',
    label: 'Buy / Sell Side',
    type: 'string',
    options: ['buy', 'sell', 'mid']
  },
  {
    key: 'side_bid_ask',
    label: 'Bid / Ask Side',
    type: 'string',
    options: ['Bid', 'Ask', 'Mid']
  },
  {
    key: 'unusual_activity_score',
    label: 'Unusual Activity Score',
    type: 'number',
    min: 0,
    max: 100,
    step: 0.1
  },
  {
    key: 'trade_count',
    label: 'Trade Count',
    type: 'number',
    min: 0,
    step: 1
  },
  {
    key: 'total_trade_size',
    label: 'Total Trade Size',
    type: 'number',
    min: 0,
    step: 0.01
  },
  {
    key: 'avg_trade_price',
    label: 'Average Trade Price',
    type: 'number',
    min: 0,
    step: 0.01
  },
  
  // Greeks fields
  {
    key: 'gamma',
    label: 'Gamma',
    type: 'number',
    step: 0.0001
  },
  {
    key: 'delta',
    label: 'Delta',
    type: 'number',
    min: -1,
    max: 1,
    step: 0.01
  },
  {
    key: 'theta',
    label: 'Theta',
    type: 'number',
    step: 0.01
  },
  {
    key: 'vega',
    label: 'Vega',
    type: 'number',
    min: 0,
    step: 0.01
  },
  {
    key: 'iv',
    label: 'Implied Volatility',
    type: 'number',
    min: 0,
    max: 10,
    step: 0.01
  },
  
  // Categorical fields
  {
    key: 'option_type',
    label: 'Option Type',
    type: 'string',
    options: ['call', 'put']
  },
  {
    key: 'moneyness',
    label: 'Moneyness',
    type: 'string',
    options: ['ITM', 'ATM', 'OTM']
  },
  {
    key: 'symbol',
    label: 'Symbol',
    type: 'string'
  },
  {
    key: 'expiry_date',
    label: 'Expiry Date',
    type: 'string'
  },
  
  // Calculated fields
  {
    key: 'bid_ask_spread',
    label: 'Bid-Ask Spread',
    type: 'number',
    min: 0,
    step: 0.01
  },
  {
    key: 'volume_imbalance',
    label: 'Volume Imbalance',
    type: 'number',
    step: 0.01
  },
  {
    key: 'put_call_ratio',
    label: 'Put/Call Ratio',
    type: 'number',
    min: 0,
    step: 0.01
  },
  
  // Time-based fields
  {
    key: 'age_minutes',
    label: 'Age (Minutes)',
    type: 'number',
    min: 0,
    step: 1
  },
  {
    key: 'created_at',
    label: 'Created At',
    type: 'string'
  },
  {
    key: 'updated_at',
    label: 'Updated At',
    type: 'string'
  }
];

// Helper function to get field by key
export const getFieldByKey = (key: string): AlertField | undefined => {
  return ALL_FIELDS.find(field => field.key === key);
};

// Helper function to get fields by type
export const getFieldsByType = (type: 'number' | 'string' | 'boolean'): AlertField[] => {
  return ALL_FIELDS.filter(field => field.type === type);
};

// Helper function to get numeric fields only
export const getNumericFields = (): AlertField[] => {
  return getFieldsByType('number');
};

// Helper function to get categorical fields only
export const getCategoricalFields = (): AlertField[] => {
  return getFieldsByType('string');
};
